// localStorage can be unavailable (private mode, blocked storage), so every access is guarded.

export function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? ({ ...fallback, ...JSON.parse(raw) } as T) : fallback;
  } catch {
    return fallback;
  }
}

export function readArray<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    const value = raw ? JSON.parse(raw) : [];
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked: preferences just won't persist */
  }
}

// crypto.randomUUID only exists on HTTPS pages; getRandomValues works everywhere.
function randomClientId(): string {
  return 'c_' + Array.from(crypto.getRandomValues(new Uint8Array(10)), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Stable random identifier for this browser; lets the server recognise reconnections. */
export function getClientId(): string {
  const KEY = 'nexo.clientId';
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = randomClientId();
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return randomClientId();
  }
}

/** Meetings created in this browser. The host key stays local and is only sent to our server. */
export interface OwnedMeeting {
  id: string;
  hostKey: string;
  passcode: string | null;
  title: string;
  startTime: string | null;
  durationMinutes: number;
  createdAt: string;
}

const OWNED_KEY = 'nexo.ownedMeetings';

export function getOwnedMeetings(): OwnedMeeting[] {
  return readArray<OwnedMeeting>(OWNED_KEY).filter((m) => typeof m?.id === 'string' && typeof m?.hostKey === 'string');
}

export function getOwnedMeeting(id: string): OwnedMeeting | undefined {
  return getOwnedMeetings().find((m) => m.id === id);
}

export function saveOwnedMeeting(meeting: OwnedMeeting): void {
  writeJson(OWNED_KEY, [meeting, ...getOwnedMeetings().filter((m) => m.id !== meeting.id)].slice(0, 50));
}

export function removeOwnedMeeting(id: string): void {
  writeJson(OWNED_KEY, getOwnedMeetings().filter((m) => m.id !== id));
}

export interface RecentMeeting {
  id: string;
  title: string;
  at: string;
}

const RECENT_KEY = 'nexo.recentMeetings';

export function getRecentMeetings(): RecentMeeting[] {
  return readArray<RecentMeeting>(RECENT_KEY);
}

export function addRecentMeeting(entry: RecentMeeting): void {
  writeJson(RECENT_KEY, [entry, ...getRecentMeetings().filter((m) => m.id !== entry.id)].slice(0, 8));
}
